import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Zap, ArrowRight, MapPin, TrendingUp, Clock, Users, Sparkles, PlusCircle,
  Heart, ChevronDown, Compass, Moon, Shuffle, Flame,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import PlanCard, { HostLedPlanCard } from '../components/PlanCard';
import UpcomingRibbon from '../components/UpcomingRibbon';

const CITIES = ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain'];

/**
 * Curated Discovery sections — order matters: For You is the hero, then the
 * time-sensitive "Tonight", then social proof (Friends Going), then
 * personalised (Interests), then engagement (Trending), then variety
 * (Surprise). Sections that come back empty are auto-hidden so the page
 * never feels sparse for new users.
 */
const SECTIONS = [
  { key: 'for_you',       title: 'For You',                icon: Compass,    iconClass: 'text-[#B6E600]', hero: true },
  { key: 'tonight',       title: 'Happening Tonight',      icon: Moon,       iconClass: 'text-[#FFB800]' },
  { key: 'friends_going', title: 'Friends Are Going',      icon: Heart,      iconClass: 'text-[#FF5C8A]' },
  { key: 'interests',     title: 'Based on Your Interests', icon: Sparkles,  iconClass: 'text-[#B6E600]' },
  { key: 'trending',      title: 'Trending Near You',      icon: TrendingUp, iconClass: 'text-[#B6E600]' },
  { key: 'surprise',      title: 'Surprise Me',            icon: Shuffle,    iconClass: 'text-[#6B6E73]' },
];

function SuggestedPeople() {
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/explore/people?q=');
        if (alive) setPeople(data.slice(0, 6));
      } catch {}
    })();
    return () => { alive = false; };
  }, []);

  if (people.length === 0) return null;

  return (
    <div data-testid="suggested-people">
      <div className="flex items-center justify-between px-4 mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-[#B6E600]" />
          <h2 className="text-lg font-bold text-[#111111] tracking-tight">Suggested Hosts</h2>
        </div>
      </div>
      <div className="flex gap-3 px-4 overflow-x-auto hide-scrollbar">
        {people.map(p => (
          <div
            key={p.id}
            onClick={() => navigate(`/user/${p.id}`)}
            className="flex-shrink-0 w-[100px] flex flex-col items-center gap-2 p-3 bg-[#FFFFFF] rounded-2xl border border-black/[0.06] cursor-pointer active:scale-95 transition-transform"
          >
            {p.profile_photo ? (
              <img loading="lazy" decoding="async" src={p.profile_photo} alt="" className="w-14 h-14 rounded-full object-cover" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#F1F0EA] flex items-center justify-center text-sm font-bold text-[#9B9EA3]">
                {(p.display_name || '?')[0]}
              </div>
            )}
            <p className="text-[11px] font-medium text-[#111111] truncate w-full text-center">{p.display_name?.split(' ')[0]}</p>
            {p.trust_level && p.trust_level !== 'new_member' && (
              <span className={`trust-badge ${p.trust_level}`} style={{fontSize: '8px', padding: '1px 6px'}}>
                {p.trust_level === 'trusted_host' ? 'Trusted' : p.trust_level === 'community_favorite' ? 'Fav' : 'Reliable'}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const CITY_STORAGE_KEY = 'asap_selected_city';

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [feed, setFeed] = useState(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCity, setSelectedCity] = useState(() => {
    try {
      const saved = localStorage.getItem(CITY_STORAGE_KEY);
      if (saved && CITIES.includes(saved)) return saved;
    } catch {}
    return user?.city || 'Abu Dhabi';
  });
  const [showCityPicker, setShowCityPicker] = useState(false);
  const [cityToast, setCityToast] = useState(null);
  const cityToastTimer = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(CITY_STORAGE_KEY, selectedCity); } catch {}
  }, [selectedCity]);

  const fetchFeed = async () => {
    if (!isFirstLoad) setRefreshing(true);
    try {
      const { data } = await api.get(`/discovery/home?city=${encodeURIComponent(selectedCity)}`);
      setFeed(data);
    } catch (err) {
      console.error('Discovery feed error:', err);
    } finally {
      setIsFirstLoad(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity]);

  // Called by PlanCard's "Not interested" menu → optimistically drop the plan
  const handleDismiss = (planId) => {
    setFeed(prev => {
      if (!prev) return prev;
      const next = { ...prev };
      SECTIONS.forEach(s => {
        if (Array.isArray(next[s.key])) next[s.key] = next[s.key].filter(p => p.id !== planId);
      });
      return next;
    });
  };

  const handleCityChange = (city) => {
    if (city === selectedCity) {
      setShowCityPicker(false);
      return;
    }
    setSelectedCity(city);
    setShowCityPicker(false);
    setCityToast(city);
    if (cityToastTimer.current) clearTimeout(cityToastTimer.current);
    cityToastTimer.current = setTimeout(() => setCityToast(null), 1800);
  };

  useEffect(() => () => {
    if (cityToastTimer.current) clearTimeout(cityToastTimer.current);
  }, []);

  const totalPlans = feed
    ? SECTIONS.reduce((a, s) => a + (feed[s.key]?.length || 0), 0)
    : 0;
  const topInterests = feed?.top_interests || [];

  if (isFirstLoad) {
    return (
      <div className="px-4 pt-4 space-y-6" data-testid="home-loading">
        <div className="skeleton h-8 w-24" />
        <div className="flex gap-4 overflow-hidden">
          {[1, 2].map(i => <div key={i} className="skeleton h-[240px] w-[260px] flex-shrink-0 rounded-3xl" />)}
        </div>
        <div className="skeleton h-8 w-32" />
        <div className="flex gap-4 overflow-hidden">
          {[1, 2].map(i => <div key={i} className="skeleton h-[240px] w-[260px] flex-shrink-0 rounded-3xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="content-area animate-fade-in" data-testid="home-page">
      {/* Header */}
      <div className="px-4 pt-5 pb-3 flex items-center justify-between">
        <h1 className="text-3xl font-black tracking-tighter text-[#111111]">asap</h1>
        <div className="relative">
          <button
            data-testid="city-selector"
            onClick={() => setShowCityPicker(!showCityPicker)}
            className="flex items-center gap-1.5 bg-[#FFFFFF] rounded-full px-3 py-1.5 border border-black/[0.06] active:scale-95 transition-transform"
          >
            <MapPin size={14} className="text-[#B6E600]" />
            <span className="text-xs font-medium text-[#111111]">{selectedCity}</span>
            <ChevronDown size={12} className="text-[#9B9EA3]" />
          </button>
          {showCityPicker && (
            <div className="absolute right-0 top-10 bg-[#FFFFFF] border border-black/[0.06] rounded-2xl shadow-xl shadow-black/5 z-50 py-2 min-w-[160px] animate-scale-in">
              {CITIES.map(c => (
                <button
                  key={c}
                  data-testid={`city-${c.toLowerCase().replace(' ', '-')}`}
                  onClick={() => handleCityChange(c)}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                    selectedCity === c ? 'text-[#B6E600] font-medium bg-[#B6E600]/5' : 'text-[#6B6E73] hover:text-[#111111]'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {cityToast && (
        <div
          className="fixed top-[max(env(safe-area-inset-top),18px)] left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 bg-[#111111] text-white rounded-full pl-3 pr-4 py-2 shadow-2xl shadow-black/20 animate-city-toast"
          data-testid="city-updated-toast"
        >
          <span className="w-6 h-6 rounded-full bg-[#B6E600] flex items-center justify-center">
            <MapPin size={12} className="text-[#111111]" strokeWidth={3} />
          </span>
          <span className="text-[12.5px] font-bold tracking-tight">{cityToast} updated</span>
        </div>
      )}

      {/* Sections */}
      <div
        key={selectedCity}
        className={`space-y-7 pb-4 transition-opacity duration-300 ${refreshing ? 'opacity-55' : 'opacity-100 animate-fade-in'}`}
        data-testid="home-sections"
      >
        <UpcomingRibbon />
        {SECTIONS.map((section, sIdx) => {
          const plans = feed?.[section.key] || [];
          if (plans.length === 0) return null;
          const Icon = section.icon;
          // Section subtitles — small touch that makes Discovery feel alive
          let subtitle = null;
          if (section.key === 'interests' && topInterests.length > 0) {
            subtitle = topInterests.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' · ');
          } else if (section.key === 'friends_going') {
            subtitle = 'Plans your people are joining';
          } else if (section.key === 'tonight') {
            subtitle = 'Starting today in ' + selectedCity;
          } else if (section.key === 'trending') {
            subtitle = `in ${selectedCity}`;
          } else if (section.key === 'surprise') {
            subtitle = 'Something different today';
          }

          return (
            <div key={section.key} data-testid={`section-${section.key}`} className="animate-fade-in" style={{animationDelay: `${sIdx * 60}ms`}}>
              <div className="flex items-start justify-between px-4 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon size={section.hero ? 20 : 18} className={section.iconClass} />
                    <h2 className={`${section.hero ? 'text-xl' : 'text-lg'} font-bold text-[#111111] tracking-tight truncate`}>
                      {section.title}
                    </h2>
                    {section.key === 'tonight' && (
                      <span className="w-2 h-2 rounded-full bg-[#FF3B30] animate-pulse" />
                    )}
                  </div>
                  {subtitle && (
                    <p className="text-[11px] text-[#9B9EA3] font-medium mt-0.5 truncate">{subtitle}</p>
                  )}
                </div>
                {plans.length >= 3 && (
                  <Link
                    to={`/feed/${section.key}?city=${encodeURIComponent(selectedCity)}`}
                    data-testid={`see-all-${section.key}`}
                    className="flex items-center gap-1 text-xs font-semibold text-[#6B6E73] active:text-[#111111] transition-colors flex-shrink-0 mt-1"
                  >
                    See all <ArrowRight size={14} />
                  </Link>
                )}
              </div>

              <div className="flex gap-3 px-4 overflow-x-auto hide-scrollbar scroll-snap-x">
                {plans.map(plan =>
                  section.key === 'friends_going'
                    ? <HostLedPlanCard key={plan.id} plan={plan} onDismiss={handleDismiss} />
                    : <PlanCard key={plan.id} plan={plan} compact onDismiss={handleDismiss} />
                )}
              </div>
            </div>
          );
        })}

        {/* Suggested Hosts between sections */}
        {totalPlans > 0 && <SuggestedPeople />}

        {/* Empty interest hint — only if logged-in user with no interests yet */}
        {!feed?.has_interests && totalPlans > 0 && (
          <button
            data-testid="empty-interests-cta"
            onClick={() => navigate('/onboarding')}
            className="mx-4 flex items-center gap-3 bg-[#B6E600]/10 border border-[#B6E600]/30 rounded-2xl px-4 py-3.5 w-[calc(100%-2rem)] active:scale-[0.99] transition-transform"
          >
            <Sparkles size={18} className="text-[#3F5200] flex-shrink-0" />
            <div className="flex-1 text-left">
              <p className="text-[13px] font-bold text-[#111111]">Make this feed yours</p>
              <p className="text-[11px] text-[#6B6E73]">Pick a few interests so we can tune your recommendations</p>
            </div>
            <ArrowRight size={16} className="text-[#3F5200] flex-shrink-0" />
          </button>
        )}

        {/* Rich Empty State */}
        {totalPlans === 0 && (
          <div className="px-4 space-y-5 py-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-[#B6E600]/10 flex items-center justify-center mx-auto mb-4 animate-float">
                <Compass size={28} className="text-[#B6E600]" />
              </div>
              <h3 className="text-xl font-bold text-[#111111] mb-1">Nothing matched today</h3>
              <p className="text-sm text-[#6B6E73]">Try a different city or be the one who hosts something</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="empty-create-plan"
                onClick={() => navigate('/create')}
                className="empty-suggestion flex flex-col items-center gap-2 text-center"
              >
                <PlusCircle size={24} className="text-[#B6E600]" />
                <span className="text-sm font-medium text-[#111111]">Create a Plan</span>
                <span className="text-[11px] text-[#9B9EA3]">Coffee, fitness, social...</span>
              </button>
              <button
                data-testid="empty-explore-people"
                onClick={() => navigate('/explore')}
                className="empty-suggestion flex flex-col items-center gap-2 text-center"
              >
                <Users size={24} className="text-[#B6E600]" />
                <span className="text-sm font-medium text-[#111111]">Find People</span>
                <span className="text-[11px] text-[#9B9EA3]">Follow hosts & friends</span>
              </button>
            </div>

            <SuggestedPeople />
          </div>
        )}
      </div>
    </div>
  );
}
